import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { translateAuthError } from '../../utils/authErrors';
import { validateSignupFields } from '../../utils/signupValidation';
import { formatPhone } from '../../utils/phoneUtils';
import { PASSWORD_MIN_LENGTH, PASSWORD_HINT } from '../../utils/passwordUtils';
import PasswordStrength from './PasswordStrength';
import PasswordInput from './PasswordInput';
import EmailVerificationNotice from './EmailVerificationNotice';
import './AuthForm.css';

const SignupForm = ({ onSwitchToLogin }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [emailSendFailed, setEmailSendFailed] = useState(false);
  const { signup } = useAuth();

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const errors = validateSignupFields({ name, email, password, confirmPassword });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setLoading(true);

    try {
      const result = await signup(email.trim(), password, {
        name: name.trim(),
        phone: phone.trim(),
      });
      setVerificationEmail(result.email);
      setEmailSendFailed(!result.emailSent);
    } catch (err) {
      const message = translateAuthError(err, 'Erro ao criar conta. Tente novamente.');
      if (err?.code === 'auth/email-already-in-use') {
        setFieldErrors({ email: message });
      } else {
        setError(message);
      }
    }

    setLoading(false);
  };

  if (verificationEmail) {
    return (
      <EmailVerificationNotice
        email={verificationEmail}
        onGoToLogin={onSwitchToLogin}
        emailSendFailed={emailSendFailed}
      />
    );
  }

  const inputClass = (field) =>
    fieldErrors[field] ? 'auth-form-input--error' : '';

  return (
    <form onSubmit={handleSubmit} className="auth-form" noValidate>
      <h2 className="auth-form-title">Criar Conta</h2>
      <p className="auth-form-subtitle">
        Preencha os campos abaixo. Campos com * são obrigatórios.
      </p>

      {error && <div className="auth-form-error">{error}</div>}

      <div className="auth-form-field">
        <label className="auth-form-label" htmlFor="signup-name">Nome completo *</label>
        <input
          id="signup-name"
          type="text"
          placeholder="Seu nome"
          value={name}
          onChange={(e) => { setName(e.target.value); clearFieldError('name'); }}
          className={`auth-form-input ${inputClass('name')}`}
          autoComplete="name"
        />
        {fieldErrors.name && <p className="auth-field-error">{fieldErrors.name}</p>}
      </div>

      <div className="auth-form-field">
        <label className="auth-form-label" htmlFor="signup-email">Email *</label>
        <input
          id="signup-email"
          type="email"
          placeholder="seu@email.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); clearFieldError('email'); }}
          className={`auth-form-input ${inputClass('email')}`}
          autoComplete="email"
        />
        {fieldErrors.email && <p className="auth-field-error">{fieldErrors.email}</p>}
      </div>

      <div className="auth-form-field">
        <label className="auth-form-label" htmlFor="signup-phone">Telefone</label>
        <input
          id="signup-phone"
          type="tel"
          placeholder="(11) 99999-9999"
          value={phone}
          onChange={(e) => setPhone(formatPhone(e.target.value))}
          className="auth-form-input"
          autoComplete="tel"
        />
      </div>

      <div className="auth-form-field">
        <label className="auth-form-label" htmlFor="signup-password">Senha *</label>
        <PasswordInput
          id="signup-password"
          placeholder="Crie uma senha"
          value={password}
          onChange={(e) => { setPassword(e.target.value); clearFieldError('password'); }}
          className={inputClass('password')}
          minLength={PASSWORD_MIN_LENGTH}
          autoComplete="new-password"
        />
        <p className="auth-form-hint auth-form-hint--field">{PASSWORD_HINT}</p>
        <PasswordStrength password={password} />
        {fieldErrors.password && <p className="auth-field-error">{fieldErrors.password}</p>}
      </div>

      <div className="auth-form-field">
        <label className="auth-form-label" htmlFor="signup-confirm">Confirmar senha *</label>
        <PasswordInput
          id="signup-confirm"
          placeholder="Repita a senha"
          value={confirmPassword}
          onChange={(e) => { setConfirmPassword(e.target.value); clearFieldError('confirmPassword'); }}
          className={inputClass('confirmPassword')}
          minLength={PASSWORD_MIN_LENGTH}
          autoComplete="new-password"
        />
        {fieldErrors.confirmPassword && (
          <p className="auth-field-error">{fieldErrors.confirmPassword}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={loading}
        className="btn btn-filled auth-form-submit"
      >
        <span>{loading ? 'Criando conta...' : 'Criar Conta'}</span>
      </button>

      <div className="auth-form-switch">
        <span>Já tem uma conta? </span>
        <button type="button" onClick={onSwitchToLogin} className="auth-form-link">
          Entrar
        </button>
      </div>
    </form>
  );
};

export default SignupForm;
